export type Fetcher<T = any> = () => Promise<T>;

class RequestMemo {
  private lastHit = new Map<string, number>();
  private inflight = new Map<string, Promise<any>>();

  /**
   * Run fetcher if:
   *  - no identical in-flight request, and
   *  - last completed fetch for this key is older than ttlMs
   * Returns:
   *  - the existing in-flight promise (if any),
   *  - a new promise (if executed),
   *  - or null if suppressed by TTL.
   */
  run<T = any>(key: string, ttlMs: number, fetcher: Fetcher<T>): Promise<T | null> {
    const now = Date.now();

    // De-dupe concurrent requests
    const existing = this.inflight.get(key);
    if (existing) return existing as Promise<T>;

    // TTL suppression
    const last = this.lastHit.get(key) ?? 0;
    if (now - last < ttlMs) {
      return Promise.resolve(null);
    }

    const p = fetcher()
      .then((res) => {
        this.lastHit.set(key, Date.now());
        return res;
      })
      .finally(() => {
        this.inflight.delete(key);
      });

    this.inflight.set(key, p);
    return p as Promise<T>;
  }
}

export const requestMemo = new RequestMemo();

// Deterministic cache key builder for queries
export function buildKey(parts: Record<string, string | string[]>) {
  const entries = Object.entries(parts)
    .map(([k, v]) => [k, Array.isArray(v) ? [...v].sort() : v] as const)
    .sort(([a], [b]) => a.localeCompare(b));
  return entries.map(([k, v]) => `${k}=${Array.isArray(v) ? v.join(",") : v}`).join("|");
}
